/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description application store
 */
import _ from 'lodash';
import log from 'loglevel';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AxiosResponse } from 'axios';

import api from 'client/index';
import menuConfig from 'configs/menu';
import { getUserFromToken, validateUserAccess } from 'utils/app';
import prepareMenuConfig from 'utils/menu';
import { WORKFLOW_STATUS } from 'app-constants/workflow';

const defaultSetupInfo: App.SetupInfo = {
	timezone: '',
	enableBatchUpload: false,
	supportEmail: '',
	stripeIdentityToken: '',
	testFormURL: '',
	version: '',
	releaseVersion: '',
	selectedApps: [],
	quickPay: { link: '', qrCode: '' },
	veritableOnboarding: { status: WORKFLOW_STATUS.completed },
};

const defaultMenuConfig: App.IMenuConfig = { data: [], menus: [], pages: [], search: [] };

const initialState: App.IState = {
	status: {
		loading: true,
		error: false,
		underMaintenance: false,
		message: 'Initializing ... ',
		accessDenied: false,
	},
	user: getUserFromToken(),
	userAccess: validateUserAccess([]),
	setupInfo: defaultSetupInfo,
	appConfig: defaultMenuConfig,
	settingsConfig: defaultMenuConfig,
	collapsed: false,
	selectedMenu: null,
};

const useAppStore = create<App.IStore>()(
	devtools(
		(set) => ({
			...initialState,
			actions: {
				setStatus: (status) => set({ status }),
				setUser: (user) => set({ user }),
				setUserAccess: (userAccess) => set({ userAccess }),
				setSetupInfo: (setupInfo) => set({ setupInfo }),
				setAppConfig: (appConfig) => set({ appConfig }),
				setSettingsConfig: (settingsConfig) => set({ settingsConfig }),
				setCollapsed: (collapsed) => set({ collapsed }),
				setSelectedMenu: (selectedMenu) => set({ selectedMenu }),
			},
		}),
		{ name: 'App Store' },
	),
);

// Store to return actions and values
const useAppStoreValues = (): App.IState => useAppStore((state) => _.omit(state, ['actions']));

const useAppStoreActions = (): App.IActions => useAppStore((state) => state.actions);

/**
 * Initialize the app
 * @returns {Promise<void>}
 */
const initialize = async (user: App.User): Promise<void> => {
	const { actions, status } = useAppStore.getState();
	const userAccess = validateUserAccess(user?.realmRoles);
	actions.setUser(user);
	actions.setUserAccess(userAccess);
	if (userAccess?.canAccessApp) {
		try {
			actions.setStatus({ ...status, message: 'Loading settings ...' });
			const { data: setupInfo } = (await api.InitialSetupInformationApi.initialSetupInfo()) as AxiosResponse<App.SetupInfo>;
			actions.setSetupInfo(setupInfo);
			actions.setAppConfig(
				prepareMenuConfig({
					setupInfo,
					config: menuConfig.app,
					userRoles: user.realmRoles,
					addLandingRoute: true,
				}),
			);
			actions.setSettingsConfig(
				prepareMenuConfig({
					setupInfo,
					config: menuConfig.settings,
					userRoles: user.realmRoles,
					addLandingRoute: true,
					isSettingRoute: true,
				}),
			);
			actions.setStatus({ ...status, loading: false, message: 'Success! Welcome back to Veritable' });
		} catch (e: any) {
			log.error(e);
			actions.setStatus({ ...status, loading: false, underMaintenance: true, message: 'Site under maintenance' });
		}
	} else {
		actions.setStatus({ ...status, loading: false, accessDenied: true, message: 'Access denied' });
	}
};

export { initialize, useAppStoreValues, useAppStoreActions };
export default useAppStore;
